import { createFileRoute } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { format } from "date-fns";
import { Download, RefreshCw, Database, AlertTriangle, HistoryIcon } from "lucide-react";
import { useIsAdmin } from "@/lib/auth";

export const Route = createFileRoute("/admin/backups")({
  head: () => ({ meta: [{ title: "Backups — Admin" }] }),
  component: BackupsPage,
});

type Backup = {
  id: string;
  created_at: string;
  backup_date: string;
  row_counts: Record<string, number>;
  size_bytes: number;
  status: string;
  error: string | null;
};

type LogRow = {
  id: string;
  created_at: string;
  admin_phone: string | null;
  action: string;
  backup_id: string | null;
  status: string;
  note: string | null;
};

function BackupsPage() {
  const qc = useQueryClient();
  const { data: isAdmin } = useIsAdmin();

  const backupsQ = useQuery({
    queryKey: ["admin_backups"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("app_backups" as any)
        .select("id,created_at,backup_date,row_counts,size_bytes,status,error")
        .order("created_at", { ascending: false })
        .limit(60);
      if (error) throw error;
      return (data ?? []) as unknown as Backup[];
    },
  });

  const logsQ = useQuery({
    queryKey: ["backup_action_logs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("backup_action_logs" as any)
        .select("*")
        .order("created_at", { ascending: false })
        .limit(30);
      if (error) throw error;
      return (data ?? []) as unknown as LogRow[];
    },
  });

  const createBackup = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc("create_app_backup" as any);
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success("Backup created");
      qc.invalidateQueries({ queryKey: ["admin_backups"] });
    },
    onError: (e: any) => toast.error(e.message ?? "Backup failed"),
  });

  async function download(id: string) {
    const { data, error } = await supabase
      .from("app_backups" as any)
      .select("id,created_at,tables,row_counts")
      .eq("id", id)
      .maybeSingle();
    if (error || !data) { toast.error("Could not fetch backup"); return; }
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `backup-${(data as any).id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const restore = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase.rpc("restore_app_settings_from_backup" as any, { _backup_id: id });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success("App settings restored");
      qc.invalidateQueries({ queryKey: ["backup_action_logs"] });
      qc.invalidateQueries({ queryKey: ["app_settings"] });
    },
    onError: (e: any) => toast.error(e.message ?? "Restore failed"),
  });

  const rows = backupsQ.data ?? [];
  const logs = logsQ.data ?? [];
  const lastFailed = rows.find((r) => r.status === "failed");

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-lg font-bold">Backup & Restore</h1>
          <p className="text-xs text-muted-foreground">
            Automatic daily snapshot • Keeps last 30 days
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => backupsQ.refetch()}>
            <RefreshCw className="mr-1 h-4 w-4" /> Refresh
          </Button>
          {isAdmin && (
            <Button size="sm" onClick={() => createBackup.mutate()} disabled={createBackup.isPending}>
              <Database className="mr-1 h-4 w-4" />
              {createBackup.isPending ? "Backing up…" : "Backup now"}
            </Button>
          )}
        </div>
      </div>

      {lastFailed && (
        <div className="flex items-start gap-2 rounded-xl border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-semibold">Recent backup failed</p>
            <p className="text-xs">{lastFailed.error ?? "Unknown error"}</p>
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-xl bg-card shadow-card">
        {backupsQ.isLoading ? (
          <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            No backups yet. Click "Backup now" to create the first one.
          </div>
        ) : (
          <ul className="divide-y">
            {rows.map((r) => {
              const kb = (r.size_bytes / 1024).toFixed(1);
              const totalRows = Object.values(r.row_counts ?? {}).reduce((a, b) => a + Number(b || 0), 0);
              return (
                <li key={r.id} className="flex flex-wrap items-center gap-3 p-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">
                      {format(new Date(r.created_at), "dd MMM yyyy, HH:mm")}
                      <span className={`ml-2 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${
                        r.status === "success" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
                      }`}>{r.status}</span>
                    </p>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                      {totalRows.toLocaleString()} rows • {kb} KB • tables: {Object.keys(r.row_counts ?? {}).length}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => download(r.id)} disabled={r.status !== "success"}>
                      <Download className="mr-1 h-4 w-4" /> Download
                    </Button>
                    {isAdmin && (
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={r.status !== "success" || restore.isPending}
                        onClick={() => {
                          if (confirm("Restore app settings from this backup? Wallet/bets/results are NOT touched.")) {
                            restore.mutate(r.id);
                          }
                        }}
                      >
                        Restore settings
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div>
        <h2 className="mb-2 flex items-center gap-2 text-sm font-bold">
          <HistoryIcon className="h-4 w-4" /> Action log
        </h2>
        <div className="overflow-hidden rounded-xl bg-card shadow-card">
          {logs.length === 0 ? (
            <div className="p-4 text-center text-xs text-muted-foreground">No actions yet.</div>
          ) : (
            <ul className="divide-y">
              {logs.map((l) => (
                <li key={l.id} className="p-3 text-xs">
                  <span className="font-semibold">{l.action}</span>
                  {" • "}
                  <span className={l.status === "success" ? "text-emerald-700" : "text-red-700"}>{l.status}</span>
                  {" • "}
                  <span className="text-muted-foreground">{format(new Date(l.created_at), "dd MMM HH:mm")}</span>
                  {l.note && <p className="mt-0.5 text-muted-foreground">{l.note}</p>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
